import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { vendorAuthApi } from '@/services/vendor.api';
import { setVendorTokens, setVendorUser } from '@/lib/vendorAuth';

/** Vendor realm login (F1) — its own credentials, separate from staff/guest. */
export function VendorLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dest =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ??
    '/vendor';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await vendorAuthApi.login(email.trim(), password);
      setVendorTokens(res.accessToken, res.refreshToken);
      setVendorUser(res.user);
      navigate(dest, { replace: true });
    } catch {
      setError('Invalid credentials');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900 p-8"
      >
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-400">
          Vendor Observability
        </div>
        <h1 className="mb-6 text-lg font-semibold text-white">Sign in</h1>

        {error && (
          <div className="mb-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <label className="mb-1 block text-xs text-slate-400" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
        />

        <label className="mb-1 block text-xs text-slate-400" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-6 w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
        />

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
